import { cn } from '@parrot-software/utils';

import type { PrivacyNoticeBlock } from '../../../domain/types';

import { PrivacyNoticeText } from './PrivacyNoticeText';

interface PrivacyNoticeBlocksProps {
  blocks: PrivacyNoticeBlock[];
}

/**
 * Renders the content blocks of a privacy-notice section: paragraphs, bulleted lists, the
 * highlighted contact block and the numbered subsections, which nest blocks of their own.
 *
 * @param {PrivacyNoticeBlocksProps} props - Component properties
 */
export const PrivacyNoticeBlocks = ({ blocks }: PrivacyNoticeBlocksProps) => (
  <>
    {blocks.map((block, index) => {
      if (block.kind === 'paragraph') {
        return (
          <p
            className={cn('mb-3.5 hyphens-auto text-justify text-[15px] leading-relaxed text-foreground', {
              'font-semibold': block.emphasis,
            })}
            key={index}
          >
            <PrivacyNoticeText value={block.text} />
          </p>
        );
      }

      if (block.kind === 'list') {
        return (
          <ul
            className="mb-3.5 list-disc pl-5 hyphens-auto text-justify text-[15px] leading-relaxed text-foreground marker:text-primary"
            key={index}
          >
            {block.items.map((item, itemIndex) => (
              <li className="mb-2" key={itemIndex}>
                {item.lead && <strong className="font-semibold">{`${item.lead} `}</strong>}
                <PrivacyNoticeText value={item.text} />
              </li>
            ))}
          </ul>
        );
      }

      if (block.kind === 'callout') {
        return (
          <div
            className="mb-4 flex flex-col items-center gap-0.5 rounded-lg bg-muted px-6 py-4 text-center text-[15px] leading-normal text-foreground"
            key={index}
          >
            {block.lines.map((line, lineIndex) => (
              <span className={cn({ 'font-semibold': line.emphasis })} key={lineIndex}>
                <PrivacyNoticeText value={line.text} />
              </span>
            ))}
          </div>
        );
      }

      return (
        <section key={index}>
          <h3 className="mb-2.5 mt-5 text-base font-semibold leading-snug tracking-tight text-primary break-after-avoid">
            {block.title}
          </h3>
          <PrivacyNoticeBlocks blocks={block.blocks} />
        </section>
      );
    })}
  </>
);
